import styled, { DefaultTheme } from 'styled-components';
import { Card, Typography, Input, Button } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(
  props: { theme?: DefaultTheme },
  darkValue: T,
  lightValue: T
): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
  background: ${(props) => getThemeValue(props, '#1A202C', '#F7FAFC')};
  box-sizing: border-box;
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

export const BackButton = styled(Button)`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
  background: ${(props) => getThemeValue(props, '#2D3748', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
  
  &:hover {
    background: ${(props) => getThemeValue(props, '#4A5568', '#EDF2F7')};
    border-color: ${(props) => getThemeValue(props, '#718096', '#CBD5E0')};
  }
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 24px !important;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const FiltersCard = styled(Card)`
  background-color: ${(props) => getThemeValue(props, '#2D3748', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 8px 24px rgba(0, 0, 0, 0.4)',
      '0 4px 12px rgba(0, 0, 0, 0.08)'
    )};
  border-radius: 12px;
  margin-bottom: 24px;
  width: 100%;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }

    @media (max-width: 480px) {
      padding: 12px;
    }
  }
`;

export const FiltersTitle = styled(Typography.Title)`
  margin: 0 0 16px 0 !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

export const FilterLabel = styled(Typography.Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  margin-bottom: 4px;
  display: block;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export const SearchInputContainer = styled.div`
  width: 100%;
  margin-top: 4px;

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

export const SearchInput = styled(Input)`
  width: 100%;
  max-width: 100%;
  border-radius: 6px;
  
  .ant-input-prefix {
    color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const StyledCard = styled(Card)`
  background-color: ${(props) => getThemeValue(props, '#2D3748', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 8px 24px rgba(0, 0, 0, 0.4)',
      '0 4px 12px rgba(0, 0, 0, 0.08)'
    )};
  border-radius: 12px;
  margin-bottom: 24px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 12px 32px rgba(0, 0, 0, 0.5)',
        '0 6px 16px rgba(0, 0, 0, 0.12)'
      )};
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const SummaryCard = styled(StyledCard)<{ borderColor?: string }>`
  border-left: 4px solid ${(props) => props.borderColor || '#4299E1'};
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SummaryLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const SummaryValue = styled(Typography.Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const SummaryValueGreen = styled(SummaryValue)`
  color: #48BB78 !important;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
  margin: 16px 0;
`;

export const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const ItemCard = styled(StyledCard)`
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const RankText = styled(Typography.Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#4299E1', '#4299E1')} !important;
`;

export const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ItemName = styled(Typography.Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CategoryBadge = styled.span`
  font-size: 12px;
  color: #4299E1;
`;

export const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TrendText = styled(Typography.Text)<{ trend: string }>`
  font-size: 14px;
  color: ${(props) => {
    if (props.trend === 'up') return '#48BB78';
    if (props.trend === 'down') return '#F56565';
    return '#A0AEC0';
  }} !important;
`;

export const MetricsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetricLabel = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#A0AEC0', '#718096')} !important;
`;

export const MetricValue = styled(Typography.Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const MetricValueGreen = styled(MetricValue)`
  color: #48BB78 !important;
`;

export const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

export const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ProgressLabel = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#A0AEC0', '#718096')} !important;
`;

export const ProgressValue = styled(Typography.Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const StyledProgress = styled.div`
  .ant-progress-bg {
    background: linear-gradient(90deg, #4299E1 0%, #63B3ED 100%);
  }
`;

export const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#4A5568', '#E2E8F0')};
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const SectionTitle = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const ItemsCount = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const PeriodCard = styled(StyledCard)`
  margin-top: 24px;
`;

export const PeriodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const PeriodItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PeriodLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#CBD5E0', '#718096')} !important;
`;

export const PeriodValue = styled(Typography.Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')} !important;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const RankCell = styled.span`
  font-weight: 700;
  color: #4299E1;
`;

export const ItemCell = styled.span`
  font-weight: 600;
`;

export const TimesSoldCell = styled.span`
  font-weight: 600;
`;

export const RevenueCell = styled.span`
  font-weight: 600;
  color: #48BB78;
`;

export const PopularityCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  span {
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => getThemeValue(props, '#ffffff', '#1A202C')};
  }
`;

export const GrowthCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  span:first-child {
    font-size: 12px;
    color: #48BB78;
  }
  
  span:last-child {
    font-size: 12px;
    color: #4299E1;
  }
`;
